import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { productsAPI, suppliersAPI, certificatesAPI } from '@/utils/api';
import { Plus, Upload, Download, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';

const Suppliers = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ name: '', address: '', contact: '' });
  const [uploadForm, setUploadForm] = useState({ certificate_type: '', supplier_id: '', file: null });

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const prodRes = await productsAPI.getOne(productId);
      setProduct(prodRes.data);

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const suppRes = await suppliersAPI.getAll(user.id || 'demo-user');
      setSuppliers(suppRes.data);

      const certRes = await certificatesAPI.getByProduct(productId);
      setCertificates(certRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await suppliersAPI.create(supplierForm, user.id || 'demo-user');
      toast.success('Fournisseur ajouté');
      setDialogOpen(false);
      setSupplierForm({ name: '', address: '', contact: '' });
      loadData();
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleUploadCertificate = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Sélectionnez un fichier');
      return;
    }

    const loadingToast = toast.loading('Upload en cours...');
    
    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('certificate_type', uploadForm.certificate_type);
      formData.append('supplier_id', uploadForm.supplier_id || '');
      formData.append('file', uploadForm.file);

      console.log('Uploading certificate:', {
        productId,
        type: uploadForm.certificate_type,
        filename: uploadForm.file.name,
        size: uploadForm.file.size
      });

      const response = await certificatesAPI.upload(formData);
      console.log('Upload response:', response.data);
      
      toast.dismiss(loadingToast);
      toast.success('Certificat ajouté avec succès');
      
      // Reset form with file input
      setUploadForm({ certificate_type: '', supplier_id: '', file: null });
      document.getElementById('cert-file-input').value = '';
      
      await loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error uploading certificate:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    }
  };

  const handleDownload = async (certificateId, filename) => {
    try {
      const res = await certificatesAPI.download(certificateId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Téléchargement lancé');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeleteCertificate = async (certificateId) => {
    if (!window.confirm('Supprimer ce certificat ?')) return;

    try {
      await certificatesAPI.delete(certificateId);
      toast.success('Certificat supprimé');
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="certificates" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">
          Fournisseurs & Certificats
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} - Gestion des fournisseurs et justificatifs
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Fournisseurs</CardTitle>
                <CardDescription>Liste de vos fournisseurs</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-add-supplier" size="sm" variant="outline" className="gap-2">
                    <Plus size={16} />
                    Nouveau
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter un fournisseur</DialogTitle>
                    <DialogDescription>Informations du fournisseur d'ingrédients</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateSupplier} className="space-y-4 mt-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Nom
                      </label>
                      <Input
                        data-testid="input-supplier-name"
                        placeholder="Ex: Aromatis"
                        value={supplierForm.name}
                        onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Adresse
                      </label>
                      <Input
                        data-testid="input-supplier-address"
                        placeholder="Adresse complète"
                        value={supplierForm.address}
                        onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Contact
                      </label>
                      <Input
                        data-testid="input-supplier-contact"
                        placeholder="Email ou téléphone"
                        value={supplierForm.contact}
                        onChange={(e) => setSupplierForm({ ...supplierForm, contact: e.target.value })}
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button data-testid="btn-save-supplier" type="submit">
                        Ajouter
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {suppliers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Aucun fournisseur</p>
            ) : (
              <div className="space-y-3">
                {suppliers.map((supplier) => (
                  <div key={supplier.id} className="p-3 border border-border rounded-md">
                    <p className="font-medium text-foreground">{supplier.name}</p>
                    {supplier.address && <p className="text-sm text-muted-foreground">{supplier.address}</p>}
                    {supplier.contact && <p className="text-sm text-muted-foreground">{supplier.contact}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Uploader un certificat</CardTitle>
            <CardDescription>Fiches techniques, certificats bio, etc.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUploadCertificate} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Type de certificat
                </label>
                <Input
                  data-testid="input-cert-type"
                  placeholder="Ex: Fiche technique, Certificat bio"
                  value={uploadForm.certificate_type}
                  onChange={(e) => setUploadForm({ ...uploadForm, certificate_type: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Fournisseur (optionnel)
                </label>
                <select
                  data-testid="select-supplier"
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  value={uploadForm.supplier_id}
                  onChange={(e) => setUploadForm({ ...uploadForm, supplier_id: e.target.value })}
                >
                  <option value="">-- Sélectionner --</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Fichier
                </label>
                <Input
                  id="cert-file-input"
                  data-testid="input-cert-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      console.log('File selected:', file.name, file.size, 'bytes');
                      setUploadForm({ ...uploadForm, file });
                    }
                  }}
                  required
                />
                {uploadForm.file && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Fichier sélectionné : {uploadForm.file.name} ({(uploadForm.file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
              <Button data-testid="btn-upload-cert" type="submit" className="w-full gap-2">
                <Upload size={18} />
                Uploader le certificat
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Certificats uploadés</CardTitle>
          <CardDescription>Liste des certificats et justificatifs</CardDescription>
        </CardHeader>
        <CardContent>
          {certificates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Aucun certificat</p>
          ) : (
            <div className="space-y-3">
              {certificates.map((cert) => (
                <div key={cert.id} className="flex items-center justify-between p-3 border border-border rounded-md">
                  <div className="flex items-center gap-3">
                    <FileText className="text-primary" size={24} strokeWidth={1.5} />
                    <div>
                      <p className="font-medium text-foreground">{cert.certificate_type}</p>
                      <p className="text-sm text-muted-foreground">{cert.filename}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`btn-download-cert-${cert.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(cert.id, cert.filename)}
                      className="gap-2"
                    >
                      <Download size={16} />
                    </Button>
                    <Button
                      data-testid={`btn-delete-cert-${cert.id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteCertificate(cert.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Suppliers;
